import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import AuthCallback from "./components/AuthCallback";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import UploadPage from "./pages/UploadPage";
import PayslipDetailPage from "./pages/PayslipDetailPage";
import HistoryPage from "./pages/HistoryPage";
import ComparePage from "./pages/ComparePage";
import CreditsPage from "./pages/CreditsPage";
import AdminPage from "./pages/AdminPage";
import BancaDatiPage from "./pages/BancaDatiPage";
import SupportPage from "./pages/SupportPage";
import ControlloSindacalePage from "./pages/ControlloSindacalePage";
import OperatorePage from "./pages/OperatorePage";
import OperatoreLeadDetailPage from "./pages/OperatoreLeadDetailPage";
import OperatoreCcnlPage from "./pages/OperatoreCcnlPage";
import VertenzaPage from "./pages/VertenzaPage";
import VertenzaDetailPage from "./pages/VertenzaDetailPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  // If user data was passed from AuthCallback, we're authenticated
  if (location.state?.user) {
    return children;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const ADMIN_EMAILS = ['konechoco@gmail.com', 'konechocoproduction@gmail.com'];
  const isAdmin = user?.role === 'admin' || (user?.email && ADMIN_EMAILS.includes(user.email));

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Operator Route Component
const OperatorRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const ADMIN_EMAILS = ['konechoco@gmail.com', 'konechocoproduction@gmail.com'];
  const isOperator = user?.role === 'admin' || user?.role === 'operator' || (user?.email && ADMIN_EMAILS.includes(user.email));

  if (!isOperator) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children, allowIfAuthenticated = false }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  // Only redirect if not explicitly allowed when authenticated
  if (isAuthenticated && !allowIfAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Router component that handles OAuth callback detection
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id during render (NOT in useEffect)
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route 
        path="/login" 
        element={
          <PublicRoute allowIfAuthenticated={true}>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/forgot-password" 
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/reset-password" 
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        } 
      />
      
      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/upload"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <UploadPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route 
        path="/payslip/:id" 
        element={
          <ProtectedRoute>
            <PayslipDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/history" 
        element={
          <ProtectedRoute>
            <HistoryPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/compare" 
        element={
          <ProtectedRoute>
            <ComparePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/vertenze" 
        element={
          <OperatorRoute>
            <VertenzaPage />
          </OperatorRoute>
        } 
      />
      <Route 
        path="/vertenza/:id" 
        element={
          <OperatorRoute>
            <VertenzaDetailPage />
          </OperatorRoute>
        } 
      />
      <Route 
        path="/credits" 
        element={
          <ProtectedRoute>
            <CreditsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        } 
      />
      <Route 
        path="/banca-dati" 
        element={
          <ProtectedRoute>
            <BancaDatiPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Support page - public */}
      <Route path="/supporto" element={<SupportPage />} />
      <Route path="/support" element={<SupportPage />} />

      {/* Funnel pubblico di consulenza busta paga - controllo gratuito, nessuna autenticazione */}
      <Route path="/controllo-sindacale" element={<ControlloSindacalePage />} />

      {/* Area consulente - richiede login, ruolo verificato dal backend */}
      <Route
        path="/operatore"
        element={
          <OperatorRoute>
            <OperatorePage />
          </OperatorRoute>
        }
      />
      <Route
        path="/operatore/leads/:id"
        element={
          <OperatorRoute>
            <OperatoreLeadDetailPage />
          </OperatorRoute>
        }
      />
      <Route
        path="/operatore/ccnl"
        element={
          <OperatorRoute>
            <OperatoreCcnlPage />
          </OperatorRoute>
        }
      />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
